import { observer } from 'mobx-react-lite';
import { attendeeConflictOverflowTooltip } from './FreeBusySummary.locstring.json';
import loc from 'owa-localize';
import * as React from 'react';
import AttendeeType from 'owa-service/lib/contract/AttendeeType';
import PersonaWithFbBadge from './PersonaWithFbBadge';
import getCalendarOwnerFromItem from 'owa-calendar-forms-common/lib/utils/getCalendarOwnerFromItem';
import { getComposeStore } from 'owa-calendar-compose-store-manager';
import shouldShowAvailabilityRow from 'owa-calendar-compose-form-store/lib/selectors/shouldShowAvailabilityRow';
import getUniqueAttendees from 'owa-calendar-forms-common/lib/utils/getUniqueAttendees';

import { ActionButton } from '@fluentui/react/lib/Button';
import { ControlIcons } from 'owa-control-icons';
import { Icon } from '@fluentui/react/lib/Icon';
import { openSchedulingAssistant } from 'owa-calendar-scheduling-assistant';
import { TooltipHost } from '@fluentui/react/lib/Tooltip';

export interface FreeBusySummaryProps {
    maxConflicts: number;
}

export interface AvailabilityPersona extends AttendeeType {
    isResource: boolean;
}

let styles = require('./FreeBusySummary.scss');

/**
 * A row of Persona Pictures that represents conflicts for a meeting.
 */
export default observer(function FreeBusySummary(props: FreeBusySummaryProps) {
    const { maxConflicts } = props;
    const { event, originalEvent } = getComposeStore();
    if (!event || !shouldShowAvailabilityRow()) {
        // Return empty div so that the transition animation works correctly
        return <div className={styles.summaryContainer} />;
    }
    const { Resources, OptionalAttendees, RequiredAttendees } = event;
    let resourcesAvailabilityPersona: AvailabilityPersona[] = getAvailabilityPersonaFromAttendee(
        Resources,
        true /* isResource */
    );
    const attendees = RequiredAttendees.concat(OptionalAttendees);
    let attendeeAvailabilityPersona: AvailabilityPersona[] = getAvailabilityPersonaFromAttendee(
        attendees,
        false /* isResource */
    );
    const organizerMailbox = getCalendarOwnerFromItem(event);
    const organizerPersona: AvailabilityPersona = {
        Mailbox: organizerMailbox,
        isResource: false,
    };
    const allPersonas = [organizerPersona].concat(
        attendeeAvailabilityPersona,
        resourcesAvailabilityPersona
    );
    const showOverflow = allPersonas.length > maxConflicts;
    const uniquePersonas = getUniqueAttendees(allPersonas);
    const personasToDisplay = showOverflow
        ? uniquePersonas.slice(0, maxConflicts - 1)
        : uniquePersonas;
    return (
        <div className={styles.summaryContainer} role="group">
            {personasToDisplay.map(attendee => (
                <PersonaWithFbBadge
                    key={attendee.Mailbox.EmailAddress}
                    attendee={attendee}
                    isResource={attendee.isResource}
                    event={event}
                    originalEvent={originalEvent}
                />
            ))}
            {showOverflow && (
                <TooltipHost
                    content={loc(attendeeConflictOverflowTooltip)}
                    className={styles.overflow_tooltip}>
                    <ActionButton
                        onClick={onOverflowAttendeesClicked}
                        className={styles.overflow}
                        ariaLabel={loc(attendeeConflictOverflowTooltip)}>
                        <div className={styles.overflowText}>
                            <p>+{uniquePersonas.length - personasToDisplay.length}</p>
                        </div>
                        <div className={styles.overflowHover}>
                            <Icon iconName={ControlIcons.ScheduleEventAction} />
                        </div>
                    </ActionButton>
                </TooltipHost>
            )}
        </div>
    );
});

function onOverflowAttendeesClicked() {
    openSchedulingAssistant(false /* isProposeNewTime */);
}

function getAvailabilityPersonaFromAttendee(attendees: AttendeeType[], isResource: boolean) {
    return attendees.map(attendee => ({ ...attendee, isResource: isResource }));
}
